import React, { useState, useEffect, useMemo } from 'react';
import CompaniesTable from './companiesTable';
import {
    Box, Container, Typography, Grid, TextField, useMediaQuery, Divider
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../../styles/StyleTheme';
import buildingsBackground from '../../assets/images/fondo-edificiospng.png';
import Loading from '../../components/Loading';
import Pagination from '@mui/material/Pagination';
import { publicAxios } from '../../apiClients';
import SelectOptionMM from '../../components/SelectOptionMM';
import MMLogo from '../../assets/banners/CM-blanco.png';
import CHMLogo from '../../assets/banners/CM-blanco.png';
import Button from '@mui/material/Button';
import InstitutionButton from '../../components/InstitutionButton';
import { generateCompanyRouteSegments } from '../../utils/slugify';
import Fuse from 'fuse.js';

const MarketMonitorCorporate = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedRegion, setSelectedRegion] = useState('');
    const [firms, setFirms] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState({ field: 'position', direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const firmsPerPage = 30;
    const isMobile = useMediaQuery('(max-width: 768px)');
    const isTablet = useMediaQuery('(max-width: 1100px)');
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const showCompactCarousel = !isDesktop;
    const sectionWidth = { xs: '100%', md: '90%', lg: '80%' };
    const CONTENT_MAX_PX = 1400;

    const fuse = useMemo(() => {
        if (firms.length === 0) return null;
        return new Fuse(firms, {
            keys: ['institutionName'],
            threshold: 0.3,
            includeScore: true,
            minMatchCharLength: 2,
            distance: 100,
            ignoreLocation: false,
            shouldSort: true,
            includeMatches: true,
            findAllMatches: false,
            useExtendedSearch: true,
        });
    }, [firms]);

    const searchFirms = (firmsList, query) => {
        if (!query.trim()) return firmsList;
        const exactMatches = firmsList.filter(firm =>
            firm.institutionName.toLowerCase().startsWith(query.toLowerCase())
        );
        if (exactMatches.length > 0) return exactMatches;
        if (fuse) {
            const fuseResults = fuse.search({
                $or: [
                    { institutionName: `^${query}` },
                    { institutionName: query }
                ]
            });
            return fuseResults.map(result => result.item);
        }
        return [];
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await publicAxios.get('/institution/market-monitor-corporate/table');
                const data = response.data;
                const firmsWithSlugs = data.map((firm) => {
                    const { companySlug, countrySlug } = generateCompanyRouteSegments({
                        institutionName: firm.institutionName,
                        countryName: firm.country?.name ?? firm.Country?.name,
                        countryId: firm.country?.id ?? firm.Country?.id,
                        companyId: firm.id,
                    });

                    return {
                        ...firm,
                        slug: companySlug,
                        countrySlug,
                    };
                });
                setFirms(firmsWithSlugs);
            } catch (error) {
                console.error('Error fetching firms:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();

        // Recuperar los filtros guardados
        const savedFilters = JSON.parse(localStorage.getItem('marketMonitorCorporateFilters'));
        if (savedFilters) {
            setSearchTerm(savedFilters.searchTerm || '');
            setSelectedCountry(savedFilters.selectedCountry || '');
            setSelectedRegion(savedFilters.selectedRegion || '');
            setCurrentPage(savedFilters.currentPage || 1);
        }
    }, []);

    const handleSearchChange = (event) => {
        const newSearchTerm = event.target.value;
        setSearchTerm(newSearchTerm);
        setCurrentPage(1);
        saveFilters(newSearchTerm, selectedCountry, selectedRegion, 1);
    };

    const handleCountryChange = (newCountryId) => {
        setSelectedCountry(newCountryId || '');
        setSelectedRegion(''); // Limpiar la región cuando se selecciona un país
        setCurrentPage(1); // Reiniciar a la primera página al aplicar filtro
        saveFilters(searchTerm, newCountryId, '', 1);
    };

    const handleRegionChange = (newRegionId) => {
        setSelectedRegion(newRegionId || '');
        setSelectedCountry('');
        setCurrentPage(1);
        saveFilters(searchTerm, '', newRegionId, 1);
    };

    const saveFilters = (search, country, region, page) => {
        localStorage.setItem('marketMonitorCorporateFilters', JSON.stringify({
            searchTerm: search,
            selectedCountry: country,
            selectedRegion: region,
            currentPage: page
        }));
    }

    const sortFirms = (firms) => {
        return [...firms].sort((a, b) => {
            if (sortBy.field === 'position') {
                const positionDiff = a.position - b.position;
                if (positionDiff === 0) {
                    return sortBy.direction === 'asc'
                        ? a.institutionName.localeCompare(b.institutionName)
                        : b.institutionName.localeCompare(a.institutionName);
                }
                return sortBy.direction === 'asc' ? positionDiff : -positionDiff;
            }

            if (sortBy.field === 'totalScore') {
                const scoreDiff = a.totalScore - b.totalScore;
                if (scoreDiff === 0) {
                    return sortBy.direction === 'desc'
                        ? a.institutionName.localeCompare(b.institutionName)
                        : b.institutionName.localeCompare(a.institutionName);
                }
                return sortBy.direction === 'asc' ? scoreDiff : -scoreDiff;
            }

            if (sortBy.field === 'institutionName') {
                return sortBy.direction === 'asc'
                    ? a.institutionName.localeCompare(b.institutionName)
                    : b.institutionName.localeCompare(a.institutionName);
            }

            if (sortBy.field === 'region') {
                return sortBy.direction === 'asc'
                    ? a.country.region.localeCompare(b.country.region)
                    : b.country.region.localeCompare(a.country.region);
            }

            if (sortBy.field === 'country') {
                return sortBy.direction === 'asc'
                    ? a.country.name.localeCompare(b.country.name)
                    : b.country.name.localeCompare(a.country.name);
            }

            return 0;
        });
    }

    const uniqueRegions = [...new Set(firms
        .filter((firm) => firm.country && firm.country.region)
        .map((firm) => firm.country.region))]
        .sort((a, b) => a.localeCompare(b))
        .map((region) => ({ id: region, name: region }));

    const uniqueCountries = selectedRegion
        ? [...new Set(firms
            .filter((firm) => firm.country && firm.country.region === selectedRegion)
            .map((firm) => firm.country.name))]
            .sort((a, b) => a.localeCompare(b))
            .map((country) => ({ id: country, name: country }))
        : [...new Set(firms
            .filter((firm) => firm.country && firm.country.name)
            .map((firm) => firm.country.name))]
            .sort((a, b) => a.localeCompare(b))
            .map((country) => ({ id: country, name: country }));

    const filteredFirms = searchFirms(firms, searchTerm).filter(
        (firm) =>
            (!selectedCountry || firm.country.name === selectedCountry) &&
            (!selectedRegion || firm.country.region === selectedRegion)
    );

    const sortedFirms = sortFirms(filteredFirms);
    const indexOfLastFirm = currentPage * firmsPerPage;
    const indexOfFirstFirm = indexOfLastFirm - firmsPerPage;
    const currentFirms = sortedFirms.slice(indexOfFirstFirm, indexOfLastFirm);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        saveFilters(searchTerm, selectedCountry, selectedRegion, value);
    };

    if (isLoading) {
        return (
            <Loading
                background="#222222"
                barTrackColor="rgba(255, 255, 255, 0.35)"
                barColor="#F5C645"
                logo={CHMLogo}
                logoAlt="Corporate Market Monitor"
            />
        );
    }

    const renderContactButton = (props = {}) => (
        <Button
            component="a"
            href="https://thelegalindustry.com/contact-us/"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            sx={{
                backgroundColor: '#F5C645',
                color: '#fff',
                borderRadius: 4,
                height: props.height ?? 30,
                width: '100%',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { backgroundColor: '#F67147' },
            }}
        >
            Contact us
        </Button>
    );

    // === Sección de filtros
    const FiltersSection = (
        <Container
            sx={{
                mt: { xs: '15px', sm: '30px' },
                mb: { xs: 2, sm: 4 },
                width: sectionWidth,
                maxWidth: `${CONTENT_MAX_PX}px`,
                px: { xs: 0, sm: 2, md: 3 },
            }}
        >
            <Grid
                container
                spacing={3}
                alignItems="center"
                sx={{ justifyContent: 'center', px: { xs: 0, md: 5 } }}
            >
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        label="Search firm..."
                        variant="outlined"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        sx={{
                            width: '100%',
                            padding: '8px 0',
                            '& .MuiOutlinedInput-root': {
                                height: '33px',
                                '& fieldset': {
                                    borderColor: 'white',
                                    borderRadius: '18px',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'white',
                                    borderRadius: '18px',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#F67147',
                                    borderRadius: '18px',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: 'white',
                                fontSize: '0.8rem',
                            },
                            '& .MuiInputBase-input': {
                                color: 'white',
                            },
                        }}
                        InputLabelProps={{ style: { color: 'white' } }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <SelectOptionMM
                        options={uniqueRegions.length > 0 ? uniqueRegions : [{ id: '', name: 'No options' }]}
                        onSelectionChange={handleRegionChange}
                        name="Filter by Region"
                        width="100%"
                        selectedOption={selectedRegion}
                        mmcolor="white"
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <SelectOptionMM
                        options={uniqueCountries.length > 0 ? uniqueCountries : [{ id: '', name: 'No options' }]}
                        onSelectionChange={handleCountryChange}
                        name="Filter by Country"
                        width="100%"
                        selectedOption={selectedCountry}
                        mmcolor="white"
                    />
                </Grid>
            </Grid>
        </Container>
    );

    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    backgroundImage: `linear-gradient(
            to bottom,
            rgba(0, 0, 0, 0.1) 0%,
            rgba(29, 29, 29, 0.93) 100%
        ), url(https://static.wixstatic.com/media/a4dcb8_875148e2b4cd499fb1991033348f2878~mv2.png/v1/fill/w_2018,h_1035,al_c,q_95,usm_0.66_1.00_0.01,enc_avif,quality_auto/a4dcb8_875148e2b4cd499fb1991033348f2878~mv2.png)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    p: { xs: 3, md: 10 },
                    textAlign: 'start',
                }}
            >
                <Box sx={{ position: 'absolute', top: isTablet ? '2%' : '4%', left: '4%' }}>
                    <InstitutionButton institution="corporate" />
                </Box>
                <Grid
                    item
                    xs={12}
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >

                    {/* Imagen */}
                    <Box
                        component="img"
                        src={MMLogo}
                        alt="Market Monitor"
                        sx={{
                            width: isMobile ? '35%' : '20%',
                            height: isMobile ? '35%' : '20%',
                            marginLeft: isMobile ? '5%' : '2%',
                        }}
                    />

                    {/* Texto */}
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'white',
                            mt: isMobile ? 1 : 3,
                            textAlign: 'center',
                            lineHeight: 1.2,
                            fontSize: isMobile ? '0.65rem' : '1.2rem',
                            width: '70%',
                        }}
                    >
                        Monitor the real-time{' '}
                        <span style={{ fontSize: '115%' }}>positioning</span> of corporations in your Country and compare it with their global{' '}
                        <span style={{ fontSize: '115%' }}>competition</span>
                    </Typography>
                </Grid>
            </Box>
            <Box
                sx={{
                    backgroundColor: '#222222',
                    display: 'flex',
                    flexDirection: 'column',
                    color: 'white',
                    py: isMobile ? 2 : 4,
                    paddingBottom: '75px',
                    overflow: 'hidden',
                    px: { xs: 1.25, sm: 0 },
                }}
            >
                <Container
                    sx={{
                        width: sectionWidth,
                        maxWidth: `${CONTENT_MAX_PX}px`,
                        mx: 'auto',
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        mb: '2%',
                        color: 'white',
                        px: { xs: 0, sm: 2, md: 3 },
                    }}
                >
                    <Box
                        component="img"
                        src="https://static.wixstatic.com/media/a4dcb8_138a3cf9e74646fa8859b416ce270028~mv2.png/v1/fill/w_389,h_117,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/WHITE%20CORPORATE%20(1).png"
                        alt="Health Industry Reviews"
                        sx={{
                            width: { xs: '55%', sm: '35%', md: '20%' },
                            height: 'auto',
                            marginRight: { md: '2%' },
                            alignSelf: { xs: 'center', md: 'flex-start' },
                            mb: { xs: 1, md: 0 },
                        }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography
                            sx={{
                                fontSize: isMobile ? '0.5rem' : '75%',
                                textAlign: isMobile ? 'center' : 'left',
                                mb: isMobile ? 1 : 0,
                                marginRight: '2%',
                            }}
                        >
                            Our AI model is fed with information coming from public sources or provided by the firms (verified by our team).
                            <br />
                            Do you want to register a new firm or update displayed information?
                        </Typography>
                    </Box>
                    {!showCompactCarousel && (
                        <Box sx={{ mt: 0.5, textAlign: { xs: 'center', md: 'left' } }}>
                            {renderContactButton()}
                        </Box>
                    )}
                </Container>

                {!showCompactCarousel && (
                    <>
                        <Divider
                            sx={{
                                borderColor: '#F5C645',
                                borderWidth: 1,
                                marginTop: '1%',
                                marginBottom: '0.2%',
                                width: '80%',
                                alignSelf: 'center',
                            }}
                        />
                        {FiltersSection}
                    </>
                )}

                {showCompactCarousel && (
                    <>
                        <Container
                            sx={{
                                width: sectionWidth,
                                maxWidth: `${CONTENT_MAX_PX}px`,
                                textAlign: 'center',
                                mb: 1,
                                px: { xs: 0, sm: 2, md: 3 },
                            }}
                        >
                            {renderContactButton({ height: 34 })}
                        </Container>

                        <Divider
                            sx={{
                                borderColor: '#F5C645',
                                borderWidth: 1,
                                mt: 1,
                                mb: 2,
                                width: '92%',
                                mx: 'auto',
                            }}
                        />

                        {FiltersSection}
                    </>
                )}

                <Container
                    sx={{
                        width: sectionWidth,
                        maxWidth: `${CONTENT_MAX_PX}px`,
                        px: { xs: 0, sm: 1, md: 2 },
                    }}
                >
                    <Grid
                        container
                        columnSpacing={2}
                        rowSpacing={3}
                        alignItems="stretch"
                        wrap="nowrap"
                    >
                        <Grid
                            item
                            xs={12}
                            sx={{
                                flex: '1 1 auto',
                                minWidth: 0,
                            }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <CompaniesTable
                                    firms={currentFirms}
                                    sortBy={sortBy}
                                    setSortBy={setSortBy}
                                />
                                <Pagination
                                    count={Math.ceil(filteredFirms.length / firmsPerPage)}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    color="primary"
                                    sx={{
                                        mt: 0,
                                        justifyContent: 'center',
                                        display: 'flex',
                                        '& .MuiPaginationItem-root': {
                                            color: 'white',
                                        },
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default MarketMonitorCorporate;
